"use client";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "@/hooks/use-toast";
import Image from "next/image";
import { createClient } from "@/utils/supabase/client";
import { useRouter } from "next/navigation";

export default function LuckyDraw({
  campaign,
  openGiftBox,
  setOpenGiftBox,
  handleBack,
  participant,
  savedCode,
  setParticipant,
  setSavedCode,
  openTable,
  setOpenTable,
  setDate,
}: {
  campaign: any;
  openGiftBox: boolean;
  setOpenGiftBox: (open: boolean) => void;
  handleBack: () => void;
  participant: any;
  savedCode: any;
  setParticipant: (participant: any) => void;
  setSavedCode: (code: any) => void;
  setDate?: (date: number) => void;
  openTable: boolean;
  setOpenTable: (open: boolean) => void;
}) {
  const router = useRouter();
  const [step, setStep] = useState<number>(1);
  const [prizes, setPrizes] = useState<any>([]);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [selectedPrizeIndex, setSelectedPrizeIndex] = useState<number | null>(
    null
  );

  const anglePerSlice = prizes.length > 0 ? 360 / prizes.length : 0;

  const weightedRandom = async () => {
    const lastPrizes = await getPrizes(campaign.id);
    const weights = lastPrizes?.map((p: any) => Math.max(0, p.quantity - p.took)) || [];
    const total = weights.reduce((sum: number, w: number) => sum + w, 0);
    if (total === 0) return null; // hết hàng

    let r = Math.random() * total;
    for (let i = 0; i < weights.length; i++) {
      r -= weights[i];
      if (r <= 0) return i;
    }
    return null;
  }

  const spinWheel = async () => {
    if (savedCode.filter((item: any) => item.status).length === 0) {
      toast({
        title: "Lỗi",
        variant: "destructive",
        description: "Bạn đã dùng hết mã để quay thưởng!",
        style: {
          color: "white",
        },
      });
      return;
    }
    if (spinning || prizes.length === 0) return;
    const supabase = await createClient();
    setSpinning(true);

    let index: number | null = null;

    // trước khi random
    const { data: alreadyWinner, error } = await supabase
      .from("winners")
      .select("prize_id")
      .contains("participant", { phone: participant.phone })
      .maybeSingle(); // trả về 1 row hoặc null

    if (error) {
      toast({
        title: "Lỗi",
        variant: "destructive",
        description: "Không kiểm tra được thông tin người chơi!",
        style: {
          color: "white",
        },
      });
      setSpinning(false);
      return;
    }
    if (alreadyWinner) {
      if (campaign?.rule === 1 || savedCode.some((c: any) => !c.status)) {
        index = prizes.findIndex((p: any) => p.name === ""); // tìm giải rỗng
      } else {
        index = prizes.findIndex((p: any) => p.id === alreadyWinner.prize_id);
      }
    } else {
      index = await weightedRandom(); // random chỉ trong giải thật
    }

    if (index === null || index < 0) {
      toast({
        variant: "destructive",
        description: "Không tìm thấy giải thưởng phù hợp!",
      });
      setSpinning(false);
      return;
    }

    const sliceAngle = anglePerSlice;
    // const randomOffset = (Math.random() - 0.5) * sliceAngle * 0.8;
    const randomOffset = 0;
    const stopAngle =
      360 - (index * sliceAngle + sliceAngle / 2 + randomOffset);

    const spins = 4 + Math.floor(Math.random() * 3);
    const finalRotation = spins * 360 + stopAngle;

    setTransitionEnabled(true);
    setRotation(finalRotation);

    setTimeout(async () => {
      setSpinning(false);
      setTransitionEnabled(false);
      setRotation(stopAngle);

      // chỉ gọi RPC khi trúng giải thật
      if (campaign?.verify?.status && prizes[index].id) {
        const { data: p, error: roundError } = await supabase.rpc(
          "set_prize_manual_with_code",
          {
            p_campaign_id: campaign.id,
            p_participant_id: participant.id,
            p_round_id: prizes[index].round_id,
            p_prize_id: alreadyWinner ? null : prizes[index].id,
            p_code_id: savedCode[savedCode.findIndex((c: any) => c.status)]?.id,
          }
        );

        if (roundError) {
          toast({
            title: "Rất tiếc",
            variant: "destructive",
            description: "Vui lòng thử lại!",
            style: {
              color: "white",
            },
          });
          return;
        }
        setSavedCode(
          p.code.filter((c: any) =>
            savedCode.map((i: any) => i.id).includes(c.id)
          )
        );
        setParticipant(p);
      } else {
        const { data: p, error: roundError } = await supabase.rpc(
          "set_prize_manual_with_code",
          {
            p_campaign_id: campaign.id,
            p_participant_id: participant.id,
            p_round_id: prizes[index].round_id,
            p_prize_id: null,
            p_code_id: savedCode[savedCode.findIndex((c: any) => c.status)]?.id,
          }
        );

        if (roundError) {
          toast({
            title: "Rất tiếc",
            variant: "destructive",
            description: "Vui lòng thử lại!",
            style: {
              color: "white",
            },
          });
          return;
        }
        setSavedCode(
          p.code.filter((c: any) =>
            savedCode.map((i: any) => i.id).includes(c.id)
          )
        );
        setParticipant(p);
      }

      setSelectedPrizeIndex(index);
      setDate?.(Date.now());
    }, 5000);
  };

  const getPrizes = async (campaignId: string) => {
    const supabase = await createClient();

    const { data: round, error: roundError } = await supabase
      .from("rounds")
      .select("id")
      .eq("campaign_id", campaignId)
      .eq("status", true)
      .lte("from_at", new Date().toISOString())
      .gte("to_at", new Date().toISOString())
      .order("from_at", { ascending: true })
      .limit(1)
      .single();

    if (roundError || !round) {
      console.error("No active round found", roundError);
      setPrizes([]);
      return;
    }

    const { data: prizes, error: prizeError } = await supabase
      .from("prizes")
      .select("*")
      .eq("round_id", round.id);

    if (prizeError) {
      console.error(prizeError);
      setPrizes([]);
      return;
    }

    setPrizes([
      ...prizes,
    ]);

    return prizes;
  };

  useEffect(() => {
    if (openGiftBox) {
      getPrizes(campaign.id);
    }
    setStep(1);
    setSelectedPrizeIndex(null);
  }, [openGiftBox]);

  return (
    <Dialog open={openGiftBox} onOpenChange={setOpenGiftBox}>
      <DialogContent
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        showCloseButton={false}
        className="bg-white p-0 overflow-hidden border-none rounded-2xl lg:max-w-[1400px] w-[calc(100vw-20px)] sm:max-w-[calc(100vw-20px)]"
      >
        <VisuallyHidden>
          <DialogTitle>Hidden Title</DialogTitle>
        </VisuallyHidden>

        <div
          style={{
            backgroundImage: `url(${
              campaign?.game?.gift_box_background?.url || ""
            })`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          className="h-[calc(100vh-80px)] max-h-[900px] text-center editor-wrapper pop-up-content popup-gift-box-content text-white lg:px-12 py-8 px-4 flex flex-col gap-16 justify-between"
        >
          <div
            onClick={() => setOpenGiftBox(false)}
            className="cursor-pointer flex items-center justify-center bg-white rounded-full w-7 h-7 absolute top-4 right-4"
          >
            <X className="w-4 h-4 text-gray-500" />
          </div>

          <div className="flex items-center justify-center h-full">
            <div className="w-[80%] h-[90%] relative flex items-center justify-center">
              <div
                onClick={() => step === 2 && spinWheel()}
                className="cursor-pointer w-[15%] h-[15%] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
              />
              <svg
                id="wheel-svg"
                viewBox="0 0 200 200"
                className="w-[95%] h-[95%] absolute"
              >
                <defs>
                  <linearGradient
                    id="prizeGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#FFE23B" />
                    <stop offset="100%" stopColor="#FFBE00" />
                  </linearGradient>
                </defs>

                <g
                  id="wheel-g"
                  transform={`translate(100, 100) rotate(${rotation})`}
                  style={{
                    transition: transitionEnabled
                      ? "transform 5s cubic-bezier(0.33, 1, 0.68, 1)"
                      : "none",
                  }}
                >
                  {prizes.map((prize: any, index: number) => {
                    const startAngle = index * anglePerSlice;
                    const endAngle = startAngle + anglePerSlice;
                    const largeArcFlag = anglePerSlice > 180 ? 1 : 0;
                    const radius = 100;
                    const x1 = radius * Math.cos((Math.PI * startAngle) / 180);
                    const y1 = radius * Math.sin((Math.PI * startAngle) / 180);
                    const x2 = radius * Math.cos((Math.PI * endAngle) / 180);
                    const y2 = radius * Math.sin((Math.PI * endAngle) / 180);

                    const textAngle = startAngle + anglePerSlice / 2;
                    const textRadius = radius * 0.6;
                    const textX =
                      textRadius * Math.cos((Math.PI * textAngle) / 180);
                    const textY =
                      textRadius * Math.sin((Math.PI * textAngle) / 180);

                    return (
                      <g key={index}>
                        <path
                          d={`M0 0 L${x1} ${y1} A${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                          fill={prize.color || "#FFBE00"} // màu riêng từng ô
                        />
                        {prize.image?.url ? (
                          <image
                            href={prize.image.url}
                            x={textX - 16}
                            y={textY - 18}
                            width="35"
                            height="35"
                            transform={`rotate(${textAngle}, ${textX}, ${textY})`}
                            preserveAspectRatio="xMidYMid meet"
                          />
                        ) : (
                          <text
                            x={textX - 5}
                            y={textY}
                            textAnchor="middle"
                            alignmentBaseline="middle"
                            fontSize="8"
                            fontWeight="bold"
                            fill="#fb2c36"
                            transform={`rotate(${textAngle}, ${textX}, ${textY})`}
                          >
                            {prize.name}
                          </text>
                        )}
                      </g>
                    );
                  })}
                </g>
              </svg>
              <Image src={campaign?.game?.gift_background?.url || ""} alt="" fill className="object-contain" />
            </div>
            {step === 1 && (
              <div className="flex flex-col gap-8 items-center">
                <div style={{ lineHeight: "1.4" }}>
                  <div dangerouslySetInnerHTML={{ __html: campaign?.game?.gift_box_content }} />
                </div>
                <div className="flex flex-col gap-4 items-center">
                  <img 
                    src={campaign?.game?.gift_button?.[0]?.url || ""}
                    alt="button" 
                    className="cursor-pointer"
                    onClick={() => setStep(2)}
                  />
                  <img
                    onClick={() => setOpenTable(true)}
                    src={campaign?.game?.gift_button?.[1]?.url || ""}
                    alt="button" 
                    className="cursor-pointer"
                  />
                </div>
              </div>
            )}
          </div>

          <Dialog
            open={selectedPrizeIndex !== null}
            onOpenChange={() => setSelectedPrizeIndex(null)}
          >
            <DialogContent
              showCloseButton={false}
              className="max-w-[auto] p-0 border-none rounded-2xl outline-none"
              onInteractOutside={(event) => {
                event.preventDefault();
              }}
            >
              <VisuallyHidden>
                <DialogTitle>Hidden Title</DialogTitle>
              </VisuallyHidden>
              <div className="text-center px-6 py-8 bg-white rounded-lg flex flex-col gap-6">
                {
                  selectedPrizeIndex && prizes[selectedPrizeIndex]?.image?.url ? (
                    <img className="w-[200px] h-[200px] mx-auto object-cover -mt-24" src={prizes[selectedPrizeIndex].image.url} alt="" />
                  ) : (
                    <img src="/banner.png" alt="" className="-mt-24" />
                  )
                }
                {selectedPrizeIndex !== null && (
                  <div className="flex flex-col gap-4 items-center">
                    <div className="flex flex-col gap-4 editor-wrapper pop-up-content popup-gift-box-content">
                      <div dangerouslySetInnerHTML={{ __html: prizes[selectedPrizeIndex].description }} />
                    </div>
                    <img
                      onClick={() => {
                        if (savedCode.filter((item: any) => item.status).length === 0) {
                          setOpenGiftBox(false);
                          setDate?.(Date.now());
                          router.push(`/#winner-list`);
                        }
                        setSelectedPrizeIndex(null)
                      }}
                      src={campaign?.game?.gift_button?.[2]?.url || ""}
                      alt="button" 
                      className="cursor-pointer"
                    />
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </DialogContent>
    </Dialog>
  );
}
